import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { SERVER_URL } from "../config";
import { XMarkIcon, ArrowDownTrayIcon, EyeIcon, PlusIcon, TrashIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import SearchableDropdown from "./SearchableDropdown";
import QuotationDocumentPreview from "./QuotationDocumentPreview";
import { buildQuotationPDF, computeRowTotals, fmt } from "./PdfQuotation";

/* ─── shared style tokens ─── */
const INPUT_CLASS =
  "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500";

const INPUT_READONLY =
  "w-full bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-500 cursor-not-allowed select-none";

/* Item grid: fixed minimums so qty / rate / GST never shrink; product gets the spare width. */
const ITEM_GRID =
  "grid grid-cols-[minmax(150px,1.2fr)_minmax(240px,2.4fr)_90px_120px_80px_150px_40px] gap-3";

const EMPTY_ITEM = { category: "", item: "", qty: 1, rate: "", gstPct: 18 };

const FieldLabel = ({ children }) => (
  <label className="text-xs text-slate-500 font-medium">{children}</label>
);

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-2 mb-3 mt-1">
    <div
      className="w-1 h-4 rounded-full flex-shrink-0"
      style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }}
    />
    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 m-0">
      {children}
    </p>
  </div>
);

const StatusBadge = ({ status, posted }) => {
  if (posted)
    return <span className="text-xs px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-semibold whitespace-nowrap">Posted to Tally</span>;
  if (status === "CONFIRMED")
    return <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">Confirmed</span>;
  return <span className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold">Draft</span>;
};

const HISTORY_STYLE = {
  CREATED:         { label: "Created",         dot: "bg-blue-500" },
  EDITED:          { label: "Edited",          dot: "bg-amber-500" },
  CONFIRMED:       { label: "Confirmed",       dot: "bg-emerald-500" },
  POSTED_TO_TALLY: { label: "Posted to Tally", dot: "bg-purple-500" },
};

const formatWhen = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  return isNaN(d) ? s : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const ChangeHistory = ({ history }) => (
  <>
    <SectionTitle>Change History</SectionTitle>
    <div className="rounded-xl bg-white border border-slate-200 p-4 mb-2"
      style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
      {history.length === 0 ? (
        <p className="text-xs text-slate-400 m-0">No changes recorded yet.</p>
      ) : (
        <ol className="m-0 p-0 list-none">
          {history.map((h, i) => {
            const st = HISTORY_STYLE[h.action] || { label: h.action, dot: "bg-slate-400" };
            const lines = (h.summary || "").split("\n").filter(Boolean);
            return (
              <li key={i} className="flex gap-3 pb-4 last:pb-0">
                <div className="flex flex-col items-center">
                  <span className={`w-2.5 h-2.5 rounded-full mt-1.5 ${st.dot}`} />
                  {i < history.length - 1 && <span className="flex-1 w-px bg-slate-200 mt-1" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700 m-0">
                    <span className="font-semibold">{st.label}</span>
                    {" by "}
                    <span className="font-semibold">{h.changed_by || "Unknown"}</span>
                    {h.changed_by_role === "ADMIN" && (
                      <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">Admin</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-400 m-0 mt-0.5">{formatWhen(h.createdAt)}</p>
                  {lines.length > 0 && (
                    <ul className="mt-1.5 mb-0 pl-4 text-xs text-slate-600 space-y-0.5">
                      {lines.map((l, j) => <li key={j}>{l}</li>)}
                    </ul>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  </>
);

/* ══════════════════════════════════════════════════════════════════════
   Main component
   Props:
   - isOpen, onClose
   - existingQuotationNumber (optional) → opens that quotation instead of a blank form
   - onChanged (optional)               → called after create / edit / confirm / tally post
══════════════════════════════════════════════════════════════════════ */
export default function QuotationModal({ isOpen, onClose, existingQuotationNumber, onChanged }) {
  /* ── customer / lead search ── */
  const [customerQuery, setCustomerQuery] = useState("");
  const [leads, setLeads]                 = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [showDropdown, setShowDropdown]   = useState(false);
  const [selectedLead, setSelectedLead]   = useState(null);
  const dropRef       = useRef(null);
  const skipSearchRef = useRef(false);

  /* ── quotation meta ── */
  const [subject,       setSubject]       = useState("");
  const [employeePhone, setEmployeePhone] = useState("");
  const [items, setItems]                 = useState([{ ...EMPTY_ITEM }]);
  const [termsList, setTermsList]         = useState([""]);

  /* ── saved-quotation state ── */
  const [isSaved,          setIsSaved]          = useState(false);
  const [isEditing,        setIsEditing]        = useState(false);
  const [quotationStatus,  setQuotationStatus]  = useState("DRAFT");
  const [tallyPosted,      setTallyPosted]      = useState(false);
  const [history,          setHistory]          = useState([]);
  const [loadedLeadId,     setLoadedLeadId]     = useState(null);
  const [loadedCustomer,   setLoadedCustomer]   = useState("");
  const [editSnapshot,     setEditSnapshot]     = useState(null);
  const [loading,          setLoading]          = useState(false);
  const [quotationNumber,  setQuotationNumber]  = useState("");

  /* ── submission / preview ── */
  const [submitting,  setSubmitting]  = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [submitMsg,   setSubmitMsg]   = useState("");

  /* ── all-quotations side panel ── */
  const [showList,    setShowList]    = useState(true);
  const [quotationList, setQuotationList] = useState([]);
  const [listQuery,   setListQuery]   = useState("");
  const [listLoading, setListLoading] = useState(false);

  /* ── employee from localStorage ──
     The role here only controls which buttons are shown; the server re-checks it. */
  const emp          = JSON.parse(localStorage.getItem("employee") || "{}");
  const employeeName = emp.employee_name || "";
  const isAdmin =
  String(emp.department || "").toLowerCase() === "admin" ||
  Number(emp.privilages) === 2;

  const isConfirmed  = quotationStatus === "CONFIRMED";
  const locked       = isSaved && !isEditing;                       // fields read-only
  const canEdit      = isSaved && !tallyPosted && (!isConfirmed || isAdmin);
  const canConfirm   = isSaved && !isEditing && !isConfirmed;
  const canPostTally = isAdmin && isSaved && !isEditing && isConfirmed && !tallyPosted;

  /* ── product / category master ── */
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    axios
      .get(`${SERVER_URL}/Rahul/fetchProductAndCategory.php`)
      .then((r) => {
        if (r.data?.success) {
          const list = (r.data.products || [])
            .map((p) => ({ name: p.name, category: p.service, price: p.price, status: p.status }))
            .filter((p) => p.name);
          setAllProducts(list);
        }
      })
      .catch(console.error);
  }, [isOpen]);

  const categoryOptions = useMemo(() => {
    const cats = allProducts.filter((p) => p.status === "ACTIVE").map((p) => p.category).filter(Boolean);
    return [...new Set(cats)];
  }, [allProducts]);

  const productsByCategory = useMemo(() => {
    const map = {};
    allProducts.filter((p) => p.status === "ACTIVE").forEach((p) => {
      if (!p.category || !p.name) return;
      if (!map[p.category]) map[p.category] = [];
      if (!map[p.category].some((x) => x.name === p.name)) map[p.category].push(p);
    });
    return map;
  }, [allProducts]);

  const toOpts = (arr) => arr.map((v) => ({ label: String(v), value: v }));

  /* ── fetch all leads once on open ── */
  useEffect(() => {
    if (!isOpen) return;
    axios
      .post(`${SERVER_URL}/DataFetch/leadHistory.php`, { userName: emp.employee_name, userId: emp.employee_id })
      .then((r) => { if (r.data.status === "success") setLeads(r.data.leads || []); })
      .catch(console.error);
  }, [isOpen]);

  /* ── load an existing quotation ── */
  const loadQuotation = async (qno) => {
    setLoading(true);
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/fetchQuotation.php`, { quotationNumber: qno });
      if (res.data.status !== "success") {
        setSubmitMsg(res.data.message || "Could not load quotation.");
        return false;
      }
      const q = res.data.quotation;
      setQuotationNumber(q.quotation_number);
      setSubject(q.subject || "");
      setEmployeePhone(q.employee_phone || "");
      setItems(
        q.items.length
          ? q.items.map((r) => ({
              category: r.category || "",
              item:     r.itemDescription || "",
              qty:      r.qty,
              rate:     r.rate,
              gstPct:   r.gst,
            }))
          : [{ ...EMPTY_ITEM }],
      );
      setTermsList(q.terms.length ? q.terms : [""]);
      setQuotationStatus(q.status === "CONFIRMED" ? "CONFIRMED" : "DRAFT");
      setTallyPosted(!!q.tally_posted);
      setHistory(res.data.history || []);
      setLoadedLeadId(q.lead_id);
      setLoadedCustomer(q.customerName || "");
      setIsSaved(true);
      setIsEditing(false);
      return true;
    } catch {
      setSubmitMsg("Server error while loading the quotation.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && existingQuotationNumber) loadQuotation(existingQuotationNumber);
  }, [isOpen, existingQuotationNumber]);

  /* ── all-quotations list: reloads on open and after every change (each one refreshes `history`) ── */
  useEffect(() => {
    if (!isOpen) return;
    setListLoading(true);
    axios
      .post(`${SERVER_URL}/Rahul/listQuotations.php`, { userId: emp.employee_id, userName: emp.employee_name })
      .then((r) => { if (r.data.status === "success") setQuotationList(r.data.quotations || []); })
      .catch(console.error)
      .finally(() => setListLoading(false));
  }, [isOpen, history]);

  /* resolve the lead once both the quotation and the lead list are available */
  useEffect(() => {
    if (!loadedLeadId || !leads.length) return;
    const l = leads.find((x) => String(x.lead_id) === String(loadedLeadId));
    if (l) {
      const label = l.companyName || l.contactPerson || "";
      if (label !== customerQuery) skipSearchRef.current = true;
      setSelectedLead(l);
      setCustomerQuery(label);
      setShowDropdown(false);
    }
  }, [loadedLeadId, leads]);

  /* ── filter dropdown ── */
  useEffect(() => {
    if (skipSearchRef.current) { skipSearchRef.current = false; return; }
    if (!customerQuery.trim()) { setFilteredLeads([]); setShowDropdown(false); return; }
    const q = customerQuery.toLowerCase();
    setFilteredLeads(
      leads
        .filter((c) =>
          ["companyName", "contactPerson", "mobileNumber", "mailId", "address", "product",
           "location", "branch", "referenceName", "prospect"]
            .some((k) => (c[k] || "").toLowerCase().includes(q)),
        )
        .slice(0, 10),
    );
    setShowDropdown(true);
  }, [customerQuery, leads]);

  /* ── turn the lead's product(s) into item rows, with category + rate from the product master ── */
  const buildItemsFromLead = (lead) => {
    const names = String(lead?.product || "")
      .split(/[,;|\n]/)
      .map((s) => s.trim())
      .filter(Boolean);

    const activeProducts = allProducts.filter((p) => p.status === "ACTIVE");
    const rows = [];
    const unmatched = [];

    names.forEach((n) => {
      const match = activeProducts.find((p) => p.name.trim().toLowerCase() === n.toLowerCase());
      if (!match) { unmatched.push(n); return; }
      if (rows.some((r) => r.item === match.name)) return;   // no duplicate rows
      rows.push({
        category: match.category || "",
        item:     match.name,
        qty:      1,
        rate:     match.price != null ? String(match.price) : "",   // still editable
        gstPct:   18,
      });
    });

    return { rows, unmatched };
  };

  const handleSelectLead = (lead) => {
    const label = lead.companyName || lead.contactPerson || "";
    if (label !== customerQuery) skipSearchRef.current = true;
    setSelectedLead(lead);
    setCustomerQuery(label);
    setShowDropdown(false);

    const { rows, unmatched } = buildItemsFromLead(lead);
    setItems(rows.length ? rows : [{ ...EMPTY_ITEM }]);
    setSubmitMsg(
      unmatched.length
        ? `Could not match these lead products to the product list: ${unmatched.join(", ")}. Please add them manually.`
        : "",
    );
  };

  /* ── close dropdown on outside click ── */
  useEffect(() => {
    const h = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* ── reset ── */
  const resetForm = () => {
    setCustomerQuery(""); setSelectedLead(null); setSubject(""); setEmployeePhone("");
    setItems([{ ...EMPTY_ITEM }]); setTermsList([""]);
    setIsSaved(false); setIsEditing(false); setEditSnapshot(null);
    setQuotationStatus("DRAFT"); setTallyPosted(false); setHistory([]);
    setLoadedLeadId(null); setLoadedCustomer("");
    setShowPreview(false); setSubmitMsg(""); setQuotationNumber("");
  };

  const handleClose = () => { resetForm(); onClose(); };

  if (!isOpen) return null;

  /* ── all-quotations panel helpers ── */
  const visibleList = quotationList.filter((q) => {
    const s = listQuery.toLowerCase();
    return !s || [q.quotation_number, q.customerName, q.subject, q.created_by]
      .some((v) => (v || "").toLowerCase().includes(s));
  });

  const confirmDiscard = () => !isEditing || window.confirm("Discard your unsaved changes?");

  const openFromList = async (qno) => {
    if (isSaved && qno === quotationNumber) return;
    if (!confirmDiscard()) return;
    resetForm();
    await loadQuotation(qno);
  };

  const startNew = () => {
    if (!confirmDiscard()) return;
    resetForm();
  };

  /* ── item helpers ── */
  const addItem    = () => setItems((p) => [...p, { ...EMPTY_ITEM }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const updateItem = (i, k, v) => setItems((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const handleCategoryChange = (i, category) =>
    setItems((p) => p.map((r, idx) => (idx === i ? { ...r, category, item: "", rate: "" } : r)));

  const handleProductChange = (i, productName) =>
    setItems((p) => p.map((r, idx) => {
      if (idx !== i) return r;
      const match = (productsByCategory[r.category] || []).find((x) => x.name === productName);
      return { ...r, item: productName, rate: match?.price != null ? String(match.price) : r.rate };
    }));

  /* ── terms helpers ── */
  const addTerm    = () => setTermsList((p) => [...p, ""]);
  const removeTerm = (i) => setTermsList((p) => p.filter((_, idx) => idx !== i));
  const updateTerm = (i, v) => setTermsList((p) => p.map((t, idx) => (idx === i ? v : t)));

  /* ── computed rows ── */
  const computedRows = items.map((r) => ({ ...r, ...computeRowTotals(r) }));
  const grandTotal   = computedRows.reduce((s, r) => s + r.inclTax, 0);

  /* ── validation ── */
  const isValid = () => {
    if (!isSaved && !selectedLead) return false;
    if (!subject.trim()) return false;
    return items.every((r) =>
      r.category.trim() !== "" &&
      r.item.trim() !== "" &&
      parseFloat(r.qty) > 0 &&
      r.rate !== "" && parseFloat(r.rate) >= 0,
    );
  };

  const docParams = (forEmail) => ({
    branchName: selectedLead?.branch || "",
    quotationNumber,
    customer: selectedLead || { companyName: loadedCustomer },
    rows: items,
    subject,
    employeeName,
    employeePhone,
    terms: termsList,
    forEmail,
  });

  const payloadItems = () =>
    items.map((r) => ({
      category:        r.category,
      itemDescription: r.item,
      qty:             parseFloat(r.qty) || 0,
      rate:            parseFloat(r.rate) || 0,
      gst:             parseFloat(r.gstPct) || 0,
    }));

  const who = { userId: emp.employee_id, userName: employeeName };

  /* ── create ── */
  const handleSubmit = async () => {
    if (!isValid()) {
      setSubmitMsg("Please select a lead, fill in a subject, and complete all item fields.");
      return;
    }
    setSubmitting(true); setSubmitMsg("");
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/insertQuotation.php`, {
        customerName: selectedLead?.companyName,
        leadId:       selectedLead?.lead_id,
        subject,
        employeePhone,
        items: payloadItems(),
        terms: termsList.map((t) => t.trim()).filter(Boolean),
        created_by: employeeName || emp.employee_id,
        ...who,
      });
      if (res.data.status === "success") {
        const qno = res.data.quotation_number || "";
        setQuotationNumber(qno);
        setIsSaved(true);
        await loadQuotation(qno);
        setSubmitMsg(`Quotation ${qno} saved successfully!`);
        onChanged && onChanged();
      } else {
        setSubmitMsg(res.data.message || "Failed to save quotation.");
      }
    } catch {
      setSubmitMsg("Server error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── edit flow ── */
  const startEdit = () => {
    setEditSnapshot({
      subject, employeePhone,
      items: items.map((r) => ({ ...r })),
      termsList: [...termsList],
    });
    setSubmitMsg("");
    setIsEditing(true);
  };

  const cancelEdit = () => {
    if (editSnapshot) {
      setSubject(editSnapshot.subject);
      setEmployeePhone(editSnapshot.employeePhone);
      setItems(editSnapshot.items);
      setTermsList(editSnapshot.termsList);
    }
    setSubmitMsg("");
    setIsEditing(false);
  };

  const handleSaveChanges = async () => {
    if (!isValid()) {
      setSubmitMsg("Please fill in a subject and complete all item fields.");
      return;
    }
    setSubmitting(true); setSubmitMsg("");
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/updateQuotation.php`, {
        quotationNumber,
        subject,
        employeePhone,
        items: payloadItems(),
        terms: termsList.map((t) => t.trim()).filter(Boolean),
        ...who,
      });
      if (res.data.status === "success") {
        await loadQuotation(quotationNumber);
        setSubmitMsg(res.data.unchanged ? "No changes to save." : "Changes saved successfully!");
        if (!res.data.unchanged) onChanged && onChanged();
      } else {
        setSubmitMsg(res.data.message || "Failed to save changes.");
      }
    } catch {
      setSubmitMsg("Server error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── confirm ── */
  const handleConfirm = async () => {
    if (!window.confirm("Confirm this quotation? After confirming, only an admin can edit it.")) return;
    setSubmitting(true); setSubmitMsg("");
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/confirmQuotation.php`, { quotationNumber, ...who });
      if (res.data.status === "success") {
        await loadQuotation(quotationNumber);
        setSubmitMsg("Quotation confirmed successfully!");
        onChanged && onChanged();
      } else {
        setSubmitMsg(res.data.message || "Failed to confirm quotation.");
      }
    } catch {
      setSubmitMsg("Server error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── post to Tally (admin) ── */
  const handlePostToTally = async () => {
    if (!window.confirm("Post this quotation to Tally? It cannot be edited afterwards.")) return;
    setSubmitting(true); setSubmitMsg("");
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/postQuotationToTally.php`, { quotationNumber, ...who });
      if (res.data.status === "success") {
        await loadQuotation(quotationNumber);
        setSubmitMsg("Quotation posted to Tally successfully!");
        onChanged && onChanged();
      } else {
        setSubmitMsg(res.data.message || "Failed to post to Tally.");
      }
    } catch {
      setSubmitMsg("Server error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── send email ── */
  const handleSendEmail = async () => {
    if (!selectedLead?.mailId) { setSubmitMsg("No email found for selected lead."); return; }
    setSubmitting(true); setSubmitMsg("");
    try {
      const pdfDataUri = await buildQuotationPDF(docParams(true));
      await axios.post(`${SERVER_URL}/Quotation/sendQuotation.php`, {
        toEmail:    selectedLead.mailId,
        toName:     selectedLead.companyName,
        pdfBase64:  pdfDataUri.split(",")[1],
        senderName: employeeName,
      });
      setSubmitMsg("Quotation emailed successfully!");
    } catch {
      setSubmitMsg("Failed to send email. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const msgIsGood = /success|saved|emailed|confirmed|posted/i.test(submitMsg) && !/fail|error|only an admin|could not/i.test(submitMsg);

  /* ════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════ */
  return (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(8px)" }}
    >
      <div
        className="fixed top-0 left-0 right-0 h-0.5 z-50"
        style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }}
      />

      <div
        className="w-[1500px] max-w-[98vw] max-h-[94vh] rounded-xl flex flex-col bg-white border border-slate-200"
        style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div
          className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0 bg-white rounded-t-xl"
          style={{ boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
            <div>
              <h3 className="text-lg font-bold m-0 tracking-tight text-slate-800">
                {isSaved ? `Quotation ${quotationNumber}` : "Create Quotation"}
              </h3>
              <p className="text-xs text-slate-400 m-0 mt-0.5">
                {isEditing
                  ? "Editing — changes are recorded in the change history"
                  : "Select a lead · generates PDF · saves to database · sends via email"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isSaved && <StatusBadge status={quotationStatus} posted={tallyPosted} />}
            <button type="button" onClick={() => setShowList((s) => !s)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50">
              {showList ? "Hide quotations" : "All quotations"}
            </button>
            <button type="button" onClick={handleClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* ── Side panel + main column ── */}
        <div className="flex flex-1 min-h-0">

          {showList && (
            <aside className="w-[300px] shrink-0 border-r border-slate-200 bg-white flex flex-col rounded-bl-xl">
              <div className="p-3 border-b border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-slate-700 m-0">All quotations</p>
                  <button type="button" onClick={startNew}
                    className="text-xs font-semibold text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-lg">
                    + New
                  </button>
                </div>
                <input
                  className={INPUT_CLASS}
                  placeholder="Search number, client, subject…"
                  value={listQuery}
                  onChange={(e) => setListQuery(e.target.value)}
                />
              </div>

              <div className="flex-1 overflow-y-auto">
                {listLoading && quotationList.length === 0 && (
                  <p className="p-3 text-xs text-slate-400 m-0">Loading…</p>
                )}
                {!listLoading && visibleList.length === 0 && (
                  <p className="p-3 text-xs text-slate-400 m-0">No quotations found.</p>
                )}
                {visibleList.map((q) => {
                  const active = isSaved && q.quotation_number === quotationNumber;
                  return (
                    <button key={q.quotation_number} type="button"
                      onClick={() => openFromList(q.quotation_number)}
                      className={`w-full text-left px-3 py-2.5 border-b border-slate-100 transition-colors ${active ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-slate-700 truncate">{q.quotation_number}</span>
                        <StatusBadge status={q.status} posted={q.tally_posted} />
                      </div>
                      <p className="text-xs text-slate-600 m-0 mt-0.5 truncate">{q.customerName}</p>
                      {q.subject && <p className="text-xs text-slate-400 m-0 truncate">{q.subject}</p>}
                      <p className="text-xs text-slate-400 m-0 mt-0.5">
                        ₹{fmt(q.total)} · {q.created_by} · {formatWhen(q.updatedAt)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </aside>
          )}

          <div className="flex-1 flex flex-col min-w-0 min-h-0">

            {/* ── Scrollable body ── */}
            <div
              className="flex-1 overflow-y-auto px-6 py-5"
              style={{ background: "#f0f4f8", scrollbarWidth: "thin", scrollbarColor: "rgba(148,163,184,0.5) rgba(241,245,249,1)" }}
            >
              {loading && <p className="text-xs text-slate-400 mb-3">Loading quotation…</p>}

              {isSaved && isConfirmed && !isAdmin && !tallyPosted && (
                <div className="mb-4 px-4 py-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
                  This quotation is confirmed and locked. Ask an admin if it needs to change.
                </div>
              )}
              {isSaved && tallyPosted && (
                <div className="mb-4 px-4 py-2.5 rounded-lg bg-purple-50 border border-purple-200 text-xs text-purple-700">
                  This quotation has been posted to Tally and can no longer be edited.
                </div>
              )}

              {/* ── Lead / Customer Search ── */}
              <SectionTitle>Select Lead</SectionTitle>
              <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4"
                style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>

                <div className="relative" ref={dropRef}>
                  <FieldLabel>Search by Company, Contact, Mobile, Product, Address… *</FieldLabel>
                  <input
                    className={INPUT_CLASS + " mt-1"}
                    placeholder="Type anything — Client Name, product, mobile number, location…"
                    value={customerQuery || (isSaved ? loadedCustomer : "")}
                    disabled={isSaved}   /* the lead can't be changed once a quotation exists */
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomerQuery(val);
                      if (selectedLead && val !== (selectedLead.companyName || selectedLead.contactPerson || "")) {
                        setSelectedLead(null);
                      }
                    }}
                    onFocus={() => customerQuery && setShowDropdown(true)}
                  />

                  {showDropdown && filteredLeads.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                      {filteredLeads.map((lead, i) => (
                        <div key={i}
                          className="px-4 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
                          onClick={() => handleSelectLead(lead)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-slate-700">{lead.companyName || "—"}</span>
                            {lead.product && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 font-medium">
                                {lead.product}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {lead.contactPerson} · {lead.mobileNumber}
                            {lead.location ? ` · ${lead.location}` : ""}
                            {lead.branch ? ` · ${lead.branch}` : ""}
                          </div>
                          {lead.mailId && <div className="text-xs text-blue-400">{lead.mailId}</div>}
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedLead && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-bold text-blue-800 m-0">{selectedLead.companyName}</p>
                          <p className="text-xs text-slate-500 mt-1 m-0">
                            {selectedLead.contactPerson} · {selectedLead.mobileNumber}
                            {selectedLead.alternateMobileNumber ? ` · ${selectedLead.alternateMobileNumber}` : ""}
                          </p>
                          {selectedLead.address && (
                            <p className="text-xs text-slate-500 m-0">
                              {selectedLead.address}{selectedLead.pinCode ? ` – ${selectedLead.pinCode}` : ""}
                            </p>
                          )}
                          {selectedLead.mailId && <p className="text-xs text-blue-500 m-0">{selectedLead.mailId}</p>}
                        </div>
                        {selectedLead.branch && (
                          <div className="ml-3 flex-shrink-0">
                            <span className="text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold border border-blue-200">
                              🏢 {selectedLead.branch}
                            </span>
                          </div>
                        )}
                      </div>
                      {selectedLead.prospect && (
                        <div className="mt-2 pt-2 border-t border-blue-100">
                          <span className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">Prospect: </span>
                          <span className="text-xs text-slate-600">{selectedLead.prospect}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {isSaved && !selectedLead && loadedCustomer && (
                    <p className="mt-2 text-xs text-slate-400 m-0">
                      Lead details aren't in your lead list, so the PDF will show the company name only.
                    </p>
                  )}
                </div>
              </div>

              {/* ── Quotation Details ── */}
              <SectionTitle>Quotation Details</SectionTitle>
              <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4"
                style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1 sm:col-span-2">
                    <FieldLabel>Subject *</FieldLabel>
                    <input
                      className={INPUT_CLASS}
                      placeholder="e.g. Offer for – Tally Customization."
                      value={subject}
                      disabled={locked}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <FieldLabel>Prepared By</FieldLabel>
                    <input className={INPUT_READONLY} value={employeeName} readOnly />
                  </div>

                  <div className="flex flex-col gap-1">
                    <FieldLabel>Your Contact Number</FieldLabel>
                    <input
                      className={INPUT_CLASS}
                      placeholder="e.g. 98765-43210"
                      value={employeePhone}
                      disabled={locked}
                      onChange={(e) => setEmployeePhone(e.target.value)}
                    />
                  </div>

                  {isSaved && quotationNumber && (
                    <div className="flex flex-col gap-1 sm:col-span-2">
                      <FieldLabel>Quotation Number</FieldLabel>
                      <div className="flex items-center gap-2">
                        <input className={INPUT_READONLY} value={quotationNumber} readOnly />
                        <span className="text-xs text-emerald-600 font-semibold whitespace-nowrap">✓ Saved</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Quotation Items ── */}
              <SectionTitle>Quotation Items</SectionTitle>
              <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4"
                style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>

                <div className={`${ITEM_GRID} mb-2 pb-2 border-b border-slate-100`}>
                  {["Category", "Product Description", "Qty", "Rate (₹)", "GST %", "Amount (Incl. Tax)", ""].map((h, i) => (
                    <span key={i} className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{h}</span>
                  ))}
                </div>

                {computedRows.map((row, i) => (
                  <div key={i} className={`${ITEM_GRID} mb-2 items-start`}>
                    <SearchableDropdown
                      label=""
                      options={toOpts(categoryOptions)}
                      value={row.category}
                      placeholder="Category"
                      disabled={locked}
                      onChange={(val) => handleCategoryChange(i, val)}
                    />
                    <SearchableDropdown
                      label=""
                      options={toOpts((productsByCategory[row.category] || []).map((p) => p.name))}
                      value={row.item}
                      placeholder={row.category ? "Product Description" : "Select a category first"}
                      disabled={locked || !row.category}
                      onChange={(val) => handleProductChange(i, val)}
                    />
                    <input className={INPUT_CLASS} placeholder="1" type="number" min="0"
                      disabled={locked} value={row.qty}
                      onChange={(e) => updateItem(i, "qty", e.target.value)} />
                    <input className={INPUT_CLASS} placeholder="0" type="number" min="0"
                      disabled={locked} value={row.rate}
                      onChange={(e) => updateItem(i, "rate", e.target.value)} />
                    <input className={INPUT_CLASS} placeholder="18" type="number" min="0"
                      disabled={locked} value={row.gstPct}
                      onChange={(e) => updateItem(i, "gstPct", e.target.value)} />
                    <input className={INPUT_READONLY} value={`₹${fmt(row.inclTax)}`} readOnly />
                    {items.length > 1 && !locked ? (
                      <button type="button" onClick={() => removeItem(i)}
                        className="p-2 rounded-lg text-red-400 border border-red-100 hover:bg-red-50 transition-colors">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    ) : <div className="w-8" />}
                  </div>
                ))}

                {!locked && (
                  <button type="button" onClick={addItem}
                    className="mt-1 text-xs font-semibold text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors">
                    + Add Item
                  </button>
                )}
              </div>

              {/* ── Terms & Conditions ── */}
              <SectionTitle>Terms &amp; Conditions</SectionTitle>
              <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4"
                style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                {termsList.map((term, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-slate-400 w-4 flex-shrink-0">{i + 1}.</span>
                    <input
                      className={INPUT_CLASS}
                      placeholder="e.g. GST @ 18% will be applicable on all prices mentioned above."
                      value={term}
                      disabled={locked}
                      onChange={(e) => updateTerm(i, e.target.value)}
                    />
                    {termsList.length > 1 && !locked && (
                      <button type="button" onClick={() => removeTerm(i)}
                        className="p-2 rounded-lg text-red-400 border border-red-100 hover:bg-red-50 transition-colors flex-shrink-0">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
                {!locked && (
                  <button type="button" onClick={addTerm}
                    className="mt-1 flex items-center gap-1 text-xs font-semibold text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors">
                    <PlusIcon className="h-3.5 w-3.5" /> Add Term
                  </button>
                )}
              </div>

              {/* ── Summary ── */}
              <SectionTitle>Summary</SectionTitle>
              <div className="rounded-xl bg-white border border-slate-200 overflow-hidden mb-4"
                style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      {["Category", "Item", "Qty", "Rate (₹)", "GST %", "Amount Incl. Tax (₹)"].map((h) => (
                        <th key={h} className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-2.5">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {computedRows.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-2 text-slate-600">{r.category || "—"}</td>
                        <td className="px-4 py-2 text-slate-700">{r.item || "—"}</td>
                        <td className="px-4 py-2 text-slate-600">{fmt(r.qty)}</td>
                        <td className="px-4 py-2 text-slate-600">₹{fmt(r.rate)}</td>
                        <td className="px-4 py-2 text-slate-500">{fmt(r.gst)}%</td>
                        <td className="px-4 py-2 font-semibold text-blue-700">₹{fmt(r.inclTax)}</td>
                      </tr>
                    ))}
                    <tr className="bg-blue-50">
                      <td colSpan={5} className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Grand Total</td>
                      <td className="px-4 py-3 font-bold text-blue-800 text-base">₹{fmt(grandTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* ── Change history ── */}
              {isSaved && <ChangeHistory history={history} />}
            </div>

            {/* ── Footer ── */}
            <div className="px-6 py-4 border-t border-slate-200 bg-white rounded-br-xl flex items-center gap-3 flex-wrap shrink-0"
              style={{ boxShadow: "0 -1px 4px rgba(15,23,42,0.04)" }}>
              {submitMsg && (
                <span className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${
                  msgIsGood
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-red-50 text-red-600 border border-red-200"
                }`}>
                  {submitMsg}
                </span>
              )}

              <div className="ml-auto flex items-center gap-2 flex-wrap">
                {/* create */}
                {!isSaved && (
                  <>
                    <button type="button" onClick={handleClose}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 bg-white transition-colors">
                      Cancel
                    </button>
                    <button type="button" onClick={handleSubmit}
                      disabled={submitting || !isValid()}
                      className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}>
                      {submitting ? "Saving…" : "Submit Quotation"}
                    </button>
                  </>
                )}

                {/* editing */}
                {isSaved && isEditing && (
                  <>
                    <button type="button" onClick={cancelEdit} disabled={submitting}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 bg-white transition-colors">
                      Discard Changes
                    </button>
                    <button type="button" onClick={handleSaveChanges}
                      disabled={submitting || !isValid()}
                      className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}>
                      {submitting ? "Saving…" : "Save Changes"}
                    </button>
                  </>
                )}

                {/* saved, read-only */}
                {isSaved && !isEditing && (
                  <>
                    <button type="button" onClick={handleClose}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 bg-white transition-colors">
                      Close
                    </button>
                    <button type="button" onClick={() => setShowPreview(true)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors">
                      <EyeIcon className="h-4 w-4" /> Preview
                    </button>
                    <button type="button" onClick={() => buildQuotationPDF(docParams(false))}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors">
                      <ArrowDownTrayIcon className="h-4 w-4" /> Download PDF
                    </button>
                    {canEdit && (
                      <button type="button" onClick={startEdit} disabled={submitting}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-amber-700 border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors">
                        <PencilSquareIcon className="h-4 w-4" /> Edit
                      </button>
                    )}
                    <button type="button" onClick={handleSendEmail} disabled={submitting}
                      className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-40"
                      style={{ background: "linear-gradient(135deg, #0369a1, #0e7490)" }}>
                      {submitting ? "Working…" : "Send to Email"}
                    </button>
                    {canConfirm && (
                      <button type="button" onClick={handleConfirm} disabled={submitting}
                        className="px-5 py-2 rounded-lg text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-40">
                        Confirm Quotation
                      </button>
                    )}
                    {canPostTally && (
                      <button type="button" onClick={handlePostToTally} disabled={submitting}
                        className="px-5 py-2 rounded-lg text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 transition-colors disabled:opacity-40">
                        Post to Tally
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══ Preview Overlay ══ */}
      {showPreview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ background: "rgba(15,23,42,0.72)", backdropFilter: "blur(6px)" }}>
          <div className="w-[760px] max-w-[96vw] max-h-[90vh] rounded-xl flex flex-col bg-white border border-slate-200"
            style={{ boxShadow: "0 24px 80px rgba(15,23,42,0.28)" }}>

            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                <div>
                  <h3 className="text-base font-bold m-0 text-slate-800">Quotation Preview</h3>
                  {quotationNumber && <p className="text-xs text-blue-500 m-0 mt-0.5">#{quotationNumber}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => buildQuotationPDF(docParams(false))}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors">
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" /> Download
                </button>
                <button type="button" onClick={() => setShowPreview(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6" style={{ background: "#f0f4f8" }}>
              <QuotationDocumentPreview {...docParams(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}